import React from "react"
import headshot from "../../images/headshot-bw.webp"
import heroBg from "../../images/hero-bg.webp"

export default function Bio() {
  return (
    <section style={heroStyle} id="top">
      <div style={heroInner} className="hero-grid">
        <div style={textBox} className="hero-text-box">
          <div style={photoWrap} className="hero-photo-wrap">
            <img src={headshot} alt="Agustina Giraudy" style={photoStyle} />
            <div style={photoAccent} />
          </div>
          <div className="hero-text-header">
            <h1 style={nameStyle}>Agustina<span className="name-sep"> </span>Giraudy</h1>
            <div style={taglineStyle} className="hero-tagline">
              <span>Comparative<span className="tag-suffix"> Politics</span></span>
              <span style={dotSpan}>Latin American<span className="tag-suffix"> Politics</span></span>
              <span style={dotSpan}>Subnational<span className="tag-suffix"> Politics</span></span>
            </div>
          </div>
          <div className="hero-text-body">
            <p style={bioStyle}>
              I am a Professor of Political Science at{" "}
              <a href="https://www.american.edu/sis/faculty/giraudy.cfm" target="_blank" rel="noreferrer" style={bioLink}>
                American University
              </a>
              {" "}and a Visiting Distinguished Professor at{" "}
              <a href="https://tec.mx/es/profesores/ecsg/agustina-giraudy" target="_blank" rel="noreferrer" style={bioLink}>
                Tecnológico de Monterrey
              </a>
              . My research explores why citizens across different territories experience unequal access
              to political, social, economic, and civil rights—and how these disparities shape democracy
              and governance. With a regional focus on Latin America, I study the dynamics of subnational
              and national democratic and semi-authoritarian regimes, the politics of uneven state capacity
              and public goods provision, and the integrity and accountability of political institutions.
              <br />
              <br />
              I am the director of the{" "}
              <a href="#spp" style={bioLink}>
                <strong>Subnational Politics Project</strong> (SPP)
              </a>, a collaborative initiative dedicated to compiling, generating, and disseminating
              systematic, transparent, and publicly accessible data on subnational political institutions,
              processes, and electoral outcomes across Latin America. The SPP’s central goal is to build a
              comprehensive and standardized data infrastructure that enables both detailed within-country
              analysis and robust cross-national comparisons of subnational political dynamics. By providing
              consistent and high-quality data, SPP seeks to advance scholarly and policy-oriented research
              on the political foundations and consequences of territorial inequality in the region.
              <br />
              <br />
              My work has appeared, or is forthcoming, in{" "}
              <em>
                The Journal of Politics, Studies in Comparative International Development, Latin American
                Politics and Society, Latin American Research Review, Publius: The Journal of Federalism,
                Perspectives on Politics, Regional and Federal Studies, Journal of Politics in Latin America,
                Journal of Democracy (en Español), and Environmental Politics
              </em>
              , among others, as well as in Cambridge University Press and Oxford University Press.
            </p>
            <div style={buttonsStyle} className="hero-buttons">
              <a href="/files/cv.pdf?v=2" target="_blank" rel="noreferrer" style={btnPrimary}>Download CV</a>
              <a href="https://scholar.google.com/citations?user=QxAZtYYAAAAJ&hl=en" target="_blank" rel="noreferrer" style={btnOutline}>
                Google Scholar
              </a>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .name-sep { display: inline; }
        .hero-tagline span + span::before { content: "·"; margin-right: 8px; color: var(--gold); font-weight: 700; }
        /* float-wrap photo: name + text flow around it on desktop; flow-root contains the float without clipping the accent */
        .hero-text-box { display: flow-root; }
        @media (max-width: 900px) {
          .hero-text-box { padding: 32px 30px !important; margin-left: 0 !important; }
          /* unfloat: photo becomes a centered block above the text */
          .hero-photo-wrap {
            float: none !important;
            width: 100% !important;
            max-width: 260px !important;
            margin: 0 auto 28px !important;
          }
          .hero-photo-wrap img { height: auto !important; aspect-ratio: 3 / 4 !important; }
        }
        @media (max-width: 600px) {
          #top { min-height: auto !important; background: var(--cream) !important; }
          .hero-grid { padding: 32px 16px 28px !important; }
          .hero-text-box { padding: 22px !important; }
          .hero-photo-wrap { max-width: 220px !important; margin: 0 0 20px auto !important; }
          .hero-text-header { margin-bottom: 16px !important; }
          .hero-text-header h1 { font-size: 28px !important; margin-bottom: 2px !important; white-space: nowrap !important; }
          .hero-text-header .tag-suffix { display: none !important; }
          .hero-tagline { margin-bottom: 0 !important; gap: 6px !important; font-size: 9px !important; letter-spacing: 0.08em !important; flex-wrap: nowrap !important; white-space: nowrap !important; }
          .hero-tagline span + span::before { margin-right: 4px !important; }
          .hero-buttons { flex-wrap: nowrap !important; gap: 10px !important; }
          .hero-buttons a { padding: 11px 14px !important; font-size: 10px !important; letter-spacing: 0.14em !important; white-space: nowrap !important; flex: 1 1 auto !important; text-align: center !important; }
        }
      `}</style>
    </section>
  )
}

const heroStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  paddingTop: "58px",
  background: `url(${heroBg}) center center / cover no-repeat, var(--cream)`,
}

const heroInner = {
  maxWidth: "1320px",
  margin: "0 auto",
  padding: "32px 40px 60px",
  width: "100%",
  position: "relative",
  zIndex: 1,
}

const textBox = {
  background: "rgba(242,240,235,0.93)",
  backdropFilter: "blur(6px)",
  padding: "44px 56px",
}

const nameStyle = {
  fontFamily: '"Avant Garde", sans-serif',
  fontWeight: 300,
  fontSize: "clamp(40px, 5vw, 72px)",
  letterSpacing: "0.04em",
  color: "var(--cobalt)",
  lineHeight: 1.0,
  marginBottom: "4px",
  whiteSpace: "nowrap",
}

const taglineStyle = {
  fontFamily: '"Avant Garde", sans-serif',
  fontWeight: 400,
  fontSize: "12px",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--gold)",
  marginBottom: "40px",
  display: "flex",
  gap: "16px",
  flexWrap: "wrap",
}

const dotSpan = {
  position: "relative",
}

const bioStyle = {
  fontSize: "18px",
  lineHeight: 1.75,
  color: "var(--ink)",
  marginBottom: "40px",
  textAlign: "justify",
}

const bioLink = {
  color: "var(--cobalt-mid)",
  textDecoration: "none",
  borderBottom: "1px solid transparent",
}

const buttonsStyle = {
  display: "flex",
  gap: "16px",
  flexWrap: "wrap",
  clear: "both",
}

const btnPrimary = {
  fontFamily: '"Avant Garde", sans-serif',
  fontWeight: 400,
  fontSize: "11px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  background: "var(--cobalt)",
  color: "white",
  padding: "13px 28px",
  textDecoration: "none",
  border: "none",
  cursor: "pointer",
  display: "inline-block",
}

const btnOutline = {
  fontFamily: '"Avant Garde", sans-serif',
  fontWeight: 400,
  fontSize: "11px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  background: "transparent",
  color: "var(--cobalt)",
  padding: "13px 28px",
  textDecoration: "none",
  border: "1px solid var(--cobalt)",
  display: "inline-block",
}

const photoWrap = {
  position: "relative",
  float: "right",
  width: "290px",
  margin: "6px 8px 24px 40px",
  shapeOutside: "margin-box",
}

const photoStyle = {
  width: "100%",
  height: "400px",
  objectFit: "cover",
  objectPosition: "center top",
  display: "block",
  marginBottom: 0,
}

const photoAccent = {
  position: "absolute",
  top: "-16px",
  right: "-16px",
  width: "100%",
  height: "100%",
  border: "2px solid var(--cobalt-mid)",
  zIndex: -1,
  pointerEvents: "none",
}
